import { computed, shallowRef, watch } from 'vue'
import { loadRouteLocation, useRouter } from 'vue-router'
import type { RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router'
import { getPageRole } from '@/config/layout'
import { useLayoutStore } from '@/stores/layout'

/**
 * Decides which route each pane of the multi pane layout renders.
 *
 * The FOLDER pane keeps showing its page (FOLDER, ASSIGNMENTS or FOLDER_LIST) while the FILE
 * route is active, so opening a file does not disturb it.
 */
export function usePaneRoutes () {
    const router = useRouter()
    const layoutStore = useLayoutStore()

    const current = computed(() => router.currentRoute.value)
    const role = computed(() => getPageRole(current.value))

    const listRoute = shallowRef<RouteLocationNormalizedLoaded | null>(null)
    const folderPaneRoute = shallowRef<RouteLocationNormalizedLoaded | null>(null)
    const filePaneRoute = computed(() => role.value === 'file' ? current.value : null)

    // lazy page components are only resolved on navigation, so panes rendering other routes must load them first
    async function load (to: RouteLocationRaw) {
        return loadRouteLocation(router.resolve(to))
    }

    if (layoutStore.mode === 'three') {
        load('/folders/').then(route => {
            listRoute.value = route
        })
    }

    let updateToken = 0

    async function updateFolderPane (route: RouteLocationNormalizedLoaded) {
        const token = ++updateToken
        const pageRole = getPageRole(route)

        if (pageRole === 'folderList') {
            // in the three pane layout the list has its own column
            folderPaneRoute.value = layoutStore.mode === 'two' ? route : null
            return
        }

        if (pageRole === 'folder') {
            folderPaneRoute.value = route
            return
        }

        const folderId = (route.params as { folderId?: string }).folderId
        const paneFolderId = (folderPaneRoute.value?.params as { folderId?: string } | undefined)?.folderId

        if (pageRole === 'file' && folderId && paneFolderId !== folderId) {
            const folderRoute = await load({ name: '/folders/[folderId]', params: { folderId } })
            if (token === updateToken) {
                folderPaneRoute.value = folderRoute
            }
        }
    }

    watch(current, updateFolderPane, { immediate: true })

    return { role, current, listRoute, folderPaneRoute, filePaneRoute }
}
